'use client';

import { Box, Button, Flex, Typography } from '@coolblue-development/freddy';
import sendLeaseRequestTrigger from '@coolblue-development/ga4-events/triggers/OrderBlock/sendLeaseRequestTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import type { Product } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { redirectToWebshopLeaseFlow } from '@utils/businessUtils/redirectToWebshopLeaseFlow';
import { useContext, useState } from 'react';

import { LeaseContext } from './Context';
import TopiLogo from './TopiLogo';

type RequestLeaseButtonProps = {
  product: Product;
  quantity?: number;
};

export default function RequestLeaseButton({ product, quantity }: RequestLeaseButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { Trans, t } = useTranslation('webshop.business.leasing.actionButton');
  const { language, subsidiary } = useRequestContext();
  const { trackEvent } = useAnalytics();

  const { customerId, isLegacyLeaseFlow } = useContext(LeaseContext);

  const handleRequestLeaseClick = async () => {
    if (isLegacyLeaseFlow) {
      redirectToWebshopLeaseFlow(product.id, language, subsidiary);
      return;
    }

    const clickId = Math.floor(Date.now() / 1000);
    const reference = `cb-bjou-${clickId}`;

    trackEvent(
      sendLeaseRequestTrigger({
        unique_id: reference
      })
    );

    try {
      const payload = {
        productId: product.id,
        quantity: quantity || 1,
        customerId: customerId || null,
        languageCode: language,
        countryCode: subsidiary
      };

      setIsLoading(true);

      const response = await fetch('/api/topi/request-lease', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.redirectUrl) {
        window.location.assign(data.redirectUrl);
        setErrorMessage(null);
        return null;
      }

      setErrorMessage(t('error'));
      return null;
    } catch (error) {
      setErrorMessage(t('error'));
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Button
        type='button'
        variant='action'
        fullWidth
        onClick={handleRequestLeaseClick}
        loading={isLoading}
      >
        {isLegacyLeaseFlow ? t('requestLease') : t('action')}
      </Button>
      {!!errorMessage && (
        <Flex mt={200}>
          <Typography color='sentimentDanger' mr={100}>
            {errorMessage}
          </Typography>
        </Flex>
      )}
      {!isLegacyLeaseFlow && (
        <Flex mt={200} alignItems='center'>
          <Typography color='interactiveSubtle' mr={100}>
            <Trans
              t={t}
              i18nKey='description'
              components={{
                topi: <TopiLogo size={0.75} />
              }}
            />
          </Typography>
        </Flex>
      )}
    </Box>
  );
}
